import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { crmService } from '../../services/crmService';

const PartnerSearchCell = ({ value, crmId, onChange, disabled }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value !== undefined && value !== searchTerm) {
      setSearchTerm(value || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearch.trim().length >= 2 && showDropdown) {
      fetchPartners(debouncedSearch);
    } else {
      setResults([]);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        const portal = document.getElementById('partner-dropdown-portal');
        if (portal && portal.contains(e.target)) return;
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updatePosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 220),
    });
  }, []);

  useEffect(() => {
    if (showDropdown) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown, updatePosition]);

  const fetchPartners = async (search) => {
    setLoading(true);
    try {
      const data = await crmService.searchProfilesByName(search);
      const approved = (data || []).filter(
        (p) => p.approval_status === 'APPROVED' || p.approval_status === undefined
      );
      setResults(
        approved.map((p) => ({
          id: p.id,
          name: p.legal_entity_name,
          shortName: p.trading_short_name,
          display: p.trading_short_name
            ? `${p.legal_entity_name} (${p.trading_short_name})`
            : p.legal_entity_name,
        }))
      );
    } catch (err) {
      console.error('Partner search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (partner) => {
    setSearchTerm(partner.name);
    setShowDropdown(false);
    onChange(partner.id, partner.name);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (e.target.value.trim() === '') {
      onChange(null, '');
    }
  };

  const shouldShowDropdown = showDropdown && debouncedSearch.trim().length >= 2;

  const dropdownContent = shouldShowDropdown && dropdownPos ? (
    <div
      id="partner-dropdown-portal"
      className="fc-partner-dropdown"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        maxWidth: 360,
        zIndex: 9999,
      }}
    >
      {loading ? (
        <div className="fc-partner-dropdown-item fc-partner-loading">Searching...</div>
      ) : results.length > 0 ? (
        results.map((p) => (
          <div
            key={p.id}
            className="fc-partner-dropdown-item"
            onClick={() => handleSelect(p)}
          >
            <span className="fc-partner-name">{p.name}</span>
            {p.shortName && (
              <span className="fc-partner-short">{p.shortName}</span>
            )}
          </div>
        ))
      ) : (
        <div className="fc-partner-dropdown-item fc-partner-empty">
          No approved partners found
        </div>
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => {
          if (searchTerm.trim().length >= 2) setShowDropdown(true);
        }}
        className="fc-input"
        placeholder="Search partner..."
        disabled={disabled}
      />
      {crmId && (
        <span className="fc-crm-badge" title={`CRM ID: ${crmId}`}>
          <i className="fas fa-link"></i>
        </span>
      )}
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default PartnerSearchCell;
